/**
 * GFG Solana devnet client.
 *
 * Anchor IDL is intentionally NOT used (Anchor version conflicts during program
 * development). Instead this file builds raw `TransactionInstruction` objects
 * with manually computed instruction discriminators
 * (sha256("global:<name>")[0..8]) and manually decodes account data using
 * DataView at known byte offsets — exactly mirroring the working Node test
 * suite the program author runs.
 */
import {
  Connection,
  PublicKey,
  Transaction,
  TransactionInstruction,
  SystemProgram,
} from '@solana/web3.js';

// SPL Token program (used for burn_tokens accounts list).
export const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');
export const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL');

// ─── Constants (server-injected fallback to hard-coded devnet values) ───────
export const DEVNET = {
  cluster: 'devnet',
  rpc: 'https://api.devnet.solana.com',
  programId: 'C5vRjZZAjG1uzcir9y2tw6dwMjH79fPwQgoKaSjEnACT',
  mint: '8dDbWZ78TRuitzWLYtdFUwfBMSWZDjyi3noKF64Cu5dK',
  seasonPda: '62VB6yUaccTdR2uXDL3J3H3cN5tjPeN6iaLGQhKTF28p',
  // Devnet $GFG mint decimals — change here if the mint is ever re-deployed.
  // Used everywhere we convert raw on-chain values → user-facing $GFG.
  decimals: 6,
};

// Convenience exports — use these instead of raw 1e6/1e9 literals so a future
// mint redeploy is a one-line change in DEVNET.decimals above.
export const DECIMALS_FACTOR = 10 ** DEVNET.decimals;            // Number, for display math
export const DECIMALS_FACTOR_BIGINT = BigInt(10 ** DEVNET.decimals); // BigInt, for tx amounts
export const fromRaw = (raw) => Number(raw) / DECIMALS_FACTOR;       // raw → UI $GFG
export const toRaw = (ui) => BigInt(Math.floor(Number(ui))) * DECIMALS_FACTOR_BIGINT; // UI → raw

// ─── Connection singleton ───────────────────────────────────────────────────
let _conn = null;
export function getConnection() {
  if (!_conn) _conn = new Connection(DEVNET.rpc, 'confirmed');
  return _conn;
}

// ─── Discriminator: sha256("global:<name>") first 8 bytes (browser-safe) ──
async function sha256(str) {
  const buf = new TextEncoder().encode(str);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return new Uint8Array(hash);
}
const _discCache = new Map();
async function discriminator(name) {
  if (_discCache.has(name)) return _discCache.get(name);
  const full = await sha256(`global:${name}`);
  const slice = full.slice(0, 8);
  _discCache.set(name, slice);
  return slice;
}

export const IX_NAMES = [
  'initialize_season',
  'initialize_player',
  'submit_sp',
  'burn_tokens',
  'settle_season',
  'pause_season',
  'record_chancellor',
  'immortalize_ghost',
];

// ─── PDA helpers ────────────────────────────────────────────────────────────
function u32le(n) {
  const b = new Uint8Array(4);
  new DataView(b.buffer).setUint32(0, n >>> 0, true);
  return b;
}
function u64le(big) {
  const b = new Uint8Array(8);
  const bi = typeof big === 'bigint' ? big : BigInt(big);
  new DataView(b.buffer).setBigUint64(0, bi, true);
  return b;
}
function i64le(big) {
  const b = new Uint8Array(8);
  const bi = typeof big === 'bigint' ? big : BigInt(big);
  new DataView(b.buffer).setBigInt64(0, bi, true);
  return b;
}
function concat(...arrs) {
  const total = arrs.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrs) { out.set(a, off); off += a.length; }
  return out;
}

export function programId() {
  return new PublicKey(DEVNET.programId);
}
export function mintPubkey() {
  return new PublicKey(DEVNET.mint);
}

export function seasonPda(seasonNumber) {
  return PublicKey.findProgramAddressSync(
    [new TextEncoder().encode('season'), u32le(seasonNumber)],
    programId(),
  )[0];
}
export function playerPda(walletPubkey) {
  const wallet = walletPubkey instanceof PublicKey ? walletPubkey : new PublicKey(walletPubkey);
  return PublicKey.findProgramAddressSync(
    [new TextEncoder().encode('player'), wallet.toBytes()],
    programId(),
  )[0];
}
export function seasonRecordPda(seasonNumber, walletPubkey) {
  const wallet = walletPubkey instanceof PublicKey ? walletPubkey : new PublicKey(walletPubkey);
  return PublicKey.findProgramAddressSync(
    [new TextEncoder().encode('record'), u32le(seasonNumber), wallet.toBytes()],
    programId(),
  )[0];
}
export function codexEntryPda(walletJudged, nonce) {
  const wallet = walletJudged instanceof PublicKey ? walletJudged : new PublicKey(walletJudged);
  return PublicKey.findProgramAddressSync(
    [new TextEncoder().encode('codex'), wallet.toBytes(), u64le(nonce)],
    programId(),
  )[0];
}
export function ghostPda(walletPubkey, seasonNumber) {
  const wallet = walletPubkey instanceof PublicKey ? walletPubkey : new PublicKey(walletPubkey);
  return PublicKey.findProgramAddressSync(
    [new TextEncoder().encode('ghost'), wallet.toBytes(), u32le(seasonNumber)],
    programId(),
  )[0];
}

// ─── Account decoders ──────────────────────────────────────────────────────
/**
 * Decode the on-chain Season account.
 * Layout (bytes after 8-byte Anchor discriminator):
 *   8   u32   season_number
 *   12  i64   start_time
 *   20  i64   end_time
 *   28  u64   burn_milestone
 *   36  u64   total_burned
 *   44  u64   reward_pool
 *   52  u64   next_reward_pool
 *   60  bool  milestone_hit
 *   61  bool  settled
 *   62  bool  paused
 *   63  pk    admin (32 bytes)
 *   95  pk    valid_mint (32 bytes)
 *   127 pk    chancellor_authority (32 bytes)
 *   159 u8    bump
 */
export function decodeSeason(rawData) {
  if (!rawData || rawData.length < 160) return null;
  const buf = rawData instanceof Uint8Array ? rawData : new Uint8Array(rawData);
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const slice = (start, end) => buf.slice(start, end);
  const pk = (start) => new PublicKey(slice(start, start + 32)).toBase58();

  return {
    season_number: dv.getUint32(8, true),
    start_time: Number(dv.getBigInt64(12, true)),
    end_time: Number(dv.getBigInt64(20, true)),
    burn_milestone: dv.getBigUint64(28, true),
    total_burned: dv.getBigUint64(36, true),
    reward_pool: dv.getBigUint64(44, true),
    next_reward_pool: dv.getBigUint64(52, true),
    milestone_hit: buf[60] === 1,
    settled: buf[61] === 1,
    paused: buf[62] === 1,
    admin: pk(63),
    valid_mint: pk(95),
    chancellor_authority: pk(127),
    bump: buf[159],
  };
}

export async function readSeason(seasonAddress = DEVNET.seasonPda) {
  const conn = getConnection();
  const info = await conn.getAccountInfo(new PublicKey(seasonAddress));
  if (!info) return null;
  const decoded = decodeSeason(info.data);
  if (!decoded) return null;
  // Convert bigints to strings for safe JSON / display.
  return {
    ...decoded,
    burn_milestone: decoded.burn_milestone.toString(),
    total_burned: decoded.total_burned.toString(),
    reward_pool: decoded.reward_pool.toString(),
    next_reward_pool: decoded.next_reward_pool.toString(),
  };
}

// SOL balance in lamports → decimal SOL.
export async function getSolBalance(walletAddress) {
  const conn = getConnection();
  const lamports = await conn.getBalance(new PublicKey(walletAddress));
  return lamports / 1e9;
}

// ─── Associated Token Account (ATA) ─────────────────────────────────────────
// Synchronous PDA derivation matching the SPL associated-token-program rule.
export function associatedTokenAddress(owner, mint = mintPubkey()) {
  const ownerPk = owner instanceof PublicKey ? owner : new PublicKey(owner);
  const mintPk = mint instanceof PublicKey ? mint : new PublicKey(mint);
  return PublicKey.findProgramAddressSync(
    [ownerPk.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mintPk.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )[0];
}

/**
 * Get a wallet's $GFG (devnet mint) balance.
 * Returns { uiAmount: number, raw: bigint } or { uiAmount: 0, raw: 0n } if no ATA exists.
 */
export async function getTokenBalance(walletAddress, mint = DEVNET.mint) {
  const conn = getConnection();
  const ata = associatedTokenAddress(new PublicKey(walletAddress), new PublicKey(mint));
  try {
    const bal = await conn.getTokenAccountBalance(ata);
    if (!bal || !bal.value) return { uiAmount: 0, raw: 0n, ata: ata.toBase58() };
    return {
      uiAmount: Number(bal.value.uiAmount || 0),
      raw: BigInt(bal.value.amount || '0'),
      decimals: bal.value.decimals,
      ata: ata.toBase58(),
    };
  } catch {
    // ATA likely uninitialized.
    return { uiAmount: 0, raw: 0n, ata: ata.toBase58() };
  }
}

// ─── Account existence check ───────────────────────────────────────────────
export async function accountExists(address) {
  const conn = getConnection();
  const info = await conn.getAccountInfo(new PublicKey(address));
  return info !== null;
}

// ─── Burn flow (high-level) ────────────────────────────────────────────────
/**
 * Build the burn_tokens instruction with the program's expected account order:
 *   1. season              (writable)
 *   2. playerAccount       (writable)
 *   3. seasonRecord        (writable)
 *   4. mint                (writable, burns reduce supply)
 *   5. playerTokenAccount  (writable)
 *   6. wallet              (signer, writable)
 *   7. tokenProgram
 *   8. systemProgram
 */
export async function buildBurnFlow({ wallet, amount, seasonNumber = 1 }) {
  const walletPk = wallet instanceof PublicKey ? wallet : new PublicKey(wallet);
  const player = playerPda(walletPk);
  const seasonAccountPk = new PublicKey(DEVNET.seasonPda);
  const record = seasonRecordPda(seasonNumber, walletPk);
  const mint = mintPubkey();
  const playerTokenAcc = associatedTokenAddress(walletPk, mint);

  // Lazy initialize_player if PDA missing.
  const conn = getConnection();
  const playerInfo = await conn.getAccountInfo(player);
  const ixs = [];
  if (!playerInfo) {
    // initialize_player accounts: [player(writable), wallet(signer/writable), systemProgram]
    ixs.push(await initializePlayerIx({
      keys: [
        { pubkey: player, isSigner: false, isWritable: true },
        { pubkey: walletPk, isSigner: true, isWritable: true },
        { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      ],
    }));
  }

  ixs.push(await burnTokensIx({
    keys: [
      { pubkey: seasonAccountPk,  isSigner: false, isWritable: true },
      { pubkey: player,           isSigner: false, isWritable: true },
      { pubkey: record,           isSigner: false, isWritable: true },
      { pubkey: mint,             isSigner: false, isWritable: true },
      { pubkey: playerTokenAcc,   isSigner: false, isWritable: true },
      { pubkey: walletPk,         isSigner: true,  isWritable: true },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    amount,
  }));

  return ixs;
}

/**
 * One-shot burn: build flow → sign → send → confirm.
 * `walletAdapter` must implement `publicKey` and `signTransaction`.
 */
export async function executeBurn({ walletAdapter, amount, seasonNumber = 1 }) {
  if (!walletAdapter?.publicKey) throw new Error('Wallet not connected');
  const conn = getConnection();
  const ixs = await buildBurnFlow({
    wallet: walletAdapter.publicKey,
    amount,
    seasonNumber,
  });
  const tx = new Transaction();
  ixs.forEach((ix) => tx.add(ix));
  tx.feePayer = walletAdapter.publicKey;
  const { blockhash, lastValidBlockHeight } = await conn.getLatestBlockhash('confirmed');
  tx.recentBlockhash = blockhash;
  const signed = await walletAdapter.signTransaction(tx);
  const sig = await conn.sendRawTransaction(signed.serialize(), {
    skipPreflight: false,
    preflightCommitment: 'confirmed',
  });
  await conn.confirmTransaction({ signature: sig, blockhash, lastValidBlockHeight }, 'confirmed');
  return sig;
}

// ─── Instruction builders (raw — no IDL) ───────────────────────────────────
// Caller is responsible for assembling the right account ordering — these are
// the data buffers + program-id wrapper. The exact account list per ix is
// passed in by the caller because it depends on derived PDAs the caller knows.
export async function buildIxData(name, ...argBuffers) {
  const disc = await discriminator(name);
  return concat(disc, ...argBuffers);
}

/**
 * burn_tokens(amount: u64)
 * Caller passes the keys array matching the program's expected account order.
 */
export async function burnTokensIx({ keys, amount }) {
  const data = await buildIxData('burn_tokens', u64le(amount));
  return new TransactionInstruction({ programId: programId(), keys, data });
}

/**
 * record_chancellor(narration_hash: [u8;32], reputation_delta: i64, nonce: u64)
 * Default arg layout — adjust if the on-chain program differs.
 */
export async function recordChancellorIx({ keys, narrationHash, reputationDelta, nonce }) {
  if (!(narrationHash instanceof Uint8Array) || narrationHash.length !== 32) {
    throw new Error('narrationHash must be 32 bytes');
  }
  const data = await buildIxData(
    'record_chancellor',
    narrationHash,
    i64le(reputationDelta || 0),
    u64le(nonce || 0),
  );
  return new TransactionInstruction({ programId: programId(), keys, data });
}

/** initialize_player() — no args. */
export async function initializePlayerIx({ keys }) {
  const data = await buildIxData('initialize_player');
  return new TransactionInstruction({ programId: programId(), keys, data });
}

/** Convenience: send a raw transaction signed by Phantom-compatible wallet. */
export async function sendIx(walletAdapter, ix) {
  const conn = getConnection();
  const tx = new Transaction().add(ix);
  tx.feePayer = walletAdapter.publicKey;
  tx.recentBlockhash = (await conn.getLatestBlockhash('confirmed')).blockhash;
  const signed = await walletAdapter.signTransaction(tx);
  const sig = await conn.sendRawTransaction(signed.serialize());
  await conn.confirmTransaction(sig, 'confirmed');
  return sig;
}

export const explorerTx = (sig) =>
  `https://explorer.solana.com/tx/${sig}?cluster=${DEVNET.cluster}`;
export const explorerAddr = (addr) =>
  `https://explorer.solana.com/address/${addr}?cluster=${DEVNET.cluster}`;

// Used in tests / docs UI.
export { SystemProgram };
